import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { selectCurrentUser, selectIsOwner } from "../../../redux/user";
import { AutoRenewalItem, AutoReplenishmentItem, AutoShipmentItem, Biobox, RefItem, SubscriptionItem, TbrItem } from "../../../types";
import { getInventoryItems } from "../../../services/customerService";
import { getBioboxes } from "../../../services/biologicalServices";
import { getAutoShipments, getSubscriptionItems, getSubscriptionTbrItems } from "../../../services/subscriptionService";
import { getAutoRenewals } from "../../../services/autoRenewalsService";
import { getItems } from "../../../services/itemService";
import { isArray } from "remeda";
import { AmalgamWasteItem, MedicalWasteItem, PharmaceuticalWasteItem } from "../wastemanagement/types";
import { NonKitItem } from "../waterline/types";
import { Box, Button, Link, Table, TableBody, TableHead, TableRow, useTheme } from "@mui/material";
import PageHeader from "../../../components/PageHeader";
import HFCard from "../../../components/HFCard";
import { StyledTableCell, StyledTableRow } from "../../../components/Table.styled";
import SubscriptionModal from "./SubscriptionModal";
import { StyledTypography } from "../../../components/Typography.styled";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { trackPromise } from 'react-promise-tracker';
import { addSuccess } from "../../../redux/alerts";
import { getUpcomingShipments } from "../shipments/shipmentService";
import useCustomerNumber from "../../../hooks/useCustomerNumber";
import CreditHoldBanner from '../../../components/CreditHoldBanner'
import useCustomer from "../../../hooks/useCustomer";

interface InventoryItems {
  amalgam: AmalgamWasteItem[];
  pharma: PharmaceuticalWasteItem[];
  mwaste: MedicalWasteItem[];
  nonKitItems: NonKitItem[];
  bioBoxes: Biobox[];
}

interface AutoRenewalMap {
  [category: string]: {
    containerType: string;
    unsubscribedItems: AutoRenewalItem[];
    items: AutoRenewalItem[];
  }
}

interface CustomerCreditHolds {
  showDEAHold: boolean;
  showCreditHold: boolean;
}

const itemCategoryMapping = {
  AIRDIS: {
    image: 'waterline',
    name: 'Air Disinfection'
  },
  Amalgam: {
    image: 'amalgam',
    name: 'Waste Management'
  },
  'Lead Waste': {
    image: 'lead-waste',
    name: 'Waste Management'
  },
  Pharma: {
    image: 'pharma',
    name: 'Waste Management'
  },
  'Sharps-RMW': {
    image: 'sharps',
    name: 'Waste Management'
  },
  'SPORE TEST': {
    image: 'biological',
    name: 'Biological Monitoring'
  },
  WATERLINE: {
    image: 'waterline',
    name: 'Waterline Management'
  }
};

const AutoRenewals = () => {
  const customer = useCustomer()

  const customerNumber = useCustomerNumber()
  const currentUser = useAppSelector(selectCurrentUser);
  const isOwner = useAppSelector(selectIsOwner);
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const [autoShipments, setAutoShipments] = useState<AutoRenewalMap>({});
  const [autoReplenishments, setAutoRelenishments] = useState<AutoReplenishmentItem[]>([]);
  const [hasAutoShipments, setHasAutoShipments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<AutoRenewalItem | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  const [customerHoldFlags, setCustomerHoldFlags] = useState<CustomerCreditHolds>({ showCreditHold: false, showDEAHold: false });

  const getData = async () => {
    try {
      const [
        bioboxes,
        autoShipmentsRes,
        subscriptions,
        tbrEligibleItems,
        inventoryItems,
        autoReplenishmentsRes
      ]: [
          Biobox[],
          AutoShipmentItem[],
          SubscriptionItem[],
          TbrItem[],
          InventoryItems,
          AutoReplenishmentItem[]
        ] = await trackPromise(Promise.all([
          getBioboxes(customerNumber),
          getAutoShipments(customerNumber),
          getSubscriptionItems(customerNumber),
          getSubscriptionTbrItems(),
          getInventoryItems(customerNumber),
          getAutoRenewals(customerNumber)
        ]));

      const amalgam = inventoryItems.amalgam;
      const pharma = inventoryItems.pharma;
      const mwaste = inventoryItems.mwaste;
      const nonKitItems = inventoryItems.nonKitItems;

      const allActiveItems = [...bioboxes, amalgam, pharma, mwaste, nonKitItems].flatMap((item: any) => item);

      const items: any[] = [];

      const itemMap = allActiveItems.reduce((res, value) => {
        if (!res[value.item_number]) {
          value.quantity = 0;
          res[value.item_number] = value;
          items.push(res[value.item_number]);
        }
        res[value.item_number].quantity += 1;

        return res;
      }, {});

      subscriptions.forEach(({ itemId }) => {
        if (itemId in itemMap) return;
        const item = { item_number: itemId };
        items.push(item);
        itemMap[itemId] = item;
      });

      if (!items.length) {
        return;
      }

      let itemNumbers = items.map(item => item.item_number);

      // // Build AutoShipments
      const refItems: RefItem[] = await getItems(customerNumber, itemNumbers);
      const autoShipmentsBuild: AutoRenewalMap = {};

      items.forEach((item) => {
        const itemIsTbrEligible = tbrEligibleItems.find(el => parseInt(el.ItemNumber, 10) === parseInt(item.item_number, 10));
        const matchedItem = refItems.find(el => parseInt(el.item_number, 10) === parseInt(item.item_number, 10));
        const subscriptionItem = isArray(subscriptions) ? subscriptions.find(el => el.itemId === item.item_number) : null;
        if (matchedItem && itemIsTbrEligible) {
          const category = matchedItem.category as keyof typeof itemCategoryMapping;

          item.category = category;
          item.name = matchedItem.name;

          if (subscriptionItem) {
            item.frequency = subscriptionItem.frequency;
            item.quantity = subscriptionItem.quantity;
            item.hold = subscriptionItem.hold;
            item.startDate = subscriptionItem.startDate;

            const nextShipment = autoShipmentsRes.filter(forecastItem => forecastItem.itemId === item.item_number)
              .sort((a, b) => a.refillPeriodID - b.refillPeriodID)[0];

            if (nextShipment) {
              item.nextShipment = nextShipment.refillPeriodName;
            }
          }
          if (!autoShipmentsBuild[category]) {

            let containerType = itemCategoryMapping[category]
              ? itemCategoryMapping[category].name
              : category;

            const container = {
              containerType,
              unsubscribedItems: [],
              items: [],
            };

            autoShipmentsBuild[category] = container;
          }
          if (item.frequency) {
            autoShipmentsBuild[category].items.push(item);
          } else {
            autoShipmentsBuild[category].unsubscribedItems.push(item);
          }
        }
      });

      setAutoRelenishments(autoReplenishmentsRes);
      if (Object.keys(autoShipmentsBuild).length) {
        setHasAutoShipments(true);
        setAutoShipments(autoShipmentsBuild);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerHoldFlags = async () => {
    const upcomingShipments = await getUpcomingShipments(customerNumber);
    let showCreditHold = false;
    let showDEAHold = false;

    const date = new Date();
    date.setMonth(date.getMonth() + 2);
    date.setDate(0);

    const endNextMonth = new Date(date);

    for (let shipment in upcomingShipments) {
      if (new Date(upcomingShipments[shipment][0].refill_period_name) < endNextMonth) {
        showCreditHold = true;

        for (let item of upcomingShipments[shipment]) {
          if (item.hasControlledSubstance) {
            showDEAHold = true;
          }
        }
      }
    }

    setCustomerHoldFlags({
      showDEAHold,
      showCreditHold
    });
  }

  useMemo(() => {
    if (customerNumber) {
      getData();
      getCustomerHoldFlags();
    }
  }, [customerNumber])

  const handleOpenModal = (item: AutoRenewalItem, isEnrolledItem: boolean) => {
    if (isEnrolledItem) {
      setEnrolled(true);
    } else {
      setEnrolled(false);
    }
    setSelectedItem(item);
    setShowSubscriptionModal(true);
  }

  const handleCloseModal = (saved = false, unEnrolled = false) => {
    setShowSubscriptionModal(false);
    setSelectedItem(null);
    if (saved) {
      getData();
      if (unEnrolled) dispatch(addSuccess('Placed item on hold'));
      if (enrolled) dispatch(addSuccess('Your Auto Renewal has been updated. Please note it may take up to 10 minutes for the changes to be reflected on this page'));
      if (!enrolled) dispatch(addSuccess('Enrolled item to Auto-Shipment'));
      setSelectedItem({} as AutoRenewalItem);
    }
  }

  return (
    <Box>
      <PageHeader headerName={'Auto-Renewals'} size={'26px'} MuiIcon={AutorenewIcon} />
      <Box sx={{ color: theme.palette.color50555b.main, padding: '15px' }}>
        {!loading && (
          <>
            <div className="m-b-lg b-b" style={{ marginBottom: '15px' }}>
              <h3>Auto-shipments</h3>
            </div>
            {
              customerHoldFlags?.showCreditHold && (
                <CreditHoldBanner customer={customer} />
              )
            }
            {hasAutoShipments ? (
              <>
                {Object.keys(autoShipments).map(category => {
                  const autoShipment = autoShipments[category];
                  return (
                    <HFCard key={category} headerText={autoShipment.containerType}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <StyledTableCell sx={{ width: '60%' }}>Item Name</StyledTableCell>
                            <StyledTableCell sx={{ width: '20%' }}>Quantity</StyledTableCell>
                            <StyledTableCell sx={{ width: '10%' }}>Frequency</StyledTableCell>
                            <StyledTableCell sx={{ width: '10%' }}>Next Shipment</StyledTableCell>
                            <StyledTableCell sx={{ width: '10%' }}></StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {autoShipment.items.map(item => (
                            <StyledTableRow key={item.item_number}>
                              <StyledTableCell sx={{ width: '60%' }}>{item.name} #{item.item_number}</StyledTableCell>
                              <StyledTableCell sx={{ width: '20%' }}>{item.quantity}</StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }}>
                                Every {item.frequency} month{item.frequency > 1 ? "s" : ""}
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }}>
                                {item.hold
                                  ? "On Hold"
                                  : item.nextShipment}
                              </StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }} align="right">
                                {(item.readOnly !== true || isOwner) && (<Button variant="contained" onClick={() => handleOpenModal(item, true)}>Edit</Button>)}
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                          {autoShipment.unsubscribedItems.map(unsubscribedItem => (
                            <StyledTableRow sx={{ width: '60%' }} key={unsubscribedItem.item_number}>
                              <StyledTableCell sx={{ width: '20%' }}>{unsubscribedItem.name} #{unsubscribedItem.item_number}</StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }}>{unsubscribedItem.quantity}</StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }}>Not Enrolled</StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }}></StyledTableCell>
                              <StyledTableCell sx={{ width: '10%' }}>
                                {isOwner && (<Button variant="contained" onClick={() => handleOpenModal(unsubscribedItem, false)}>Enroll</Button>)}
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </HFCard>
                  )
                })}
                {!isOwner && (<StyledTypography>To edit your subscription online, please contact the administrator.</StyledTypography>)}
              </>
            ) : (
              <Box sx={{ padding: '15px' }}>
                <StyledTypography>You currently have no items. <Link href="https://www.healthfirst.com/store" target="_blank">Browse our store</Link></StyledTypography>
              </Box>
            )}
            <div className="m-b-lg b-b" style={{ marginBottom: '15px' }}>
              <h3>Auto-Replenishments</h3>
            </div>
            {
              autoReplenishments.length ? (
                <HFCard headerText="Kit">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: '60%' }}>Kit Name</StyledTableCell>
                        <StyledTableCell sx={{ width: '20%' }}>Refill Type</StyledTableCell>
                        <StyledTableCell sx={{ width: '10%' }}>Next Shipment</StyledTableCell>
                        <StyledTableCell sx={{ width: '10%' }}></StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {autoReplenishments.map(kit => (
                        <StyledTableRow key={kit.kit_serial_number}>
                          <StyledTableCell sx={{ width: '60%' }}>{kit.kit_model} #{kit.kit_serial_number}</StyledTableCell>
                          <StyledTableCell sx={{ width: '20%' }}>
                            {!kit.po ? "Auto-replenishment" : "Not Automatic: on advanced approval"}
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '10%' }}>
                            {kit.RefillPeriodName}
                          </StyledTableCell>
                          <StyledTableCell sx={{ width: '10%' }}></StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Box sx={{ padding: '15px' }}>
                    <StyledTypography>To add additional items or make changes, please contact us at phone here 1-800-331-1984</StyledTypography>
                  </Box>
                </HFCard>
              ) : (
                <Box sx={{ padding: '15px' }}>
                  <StyledTypography>You currently have no items. <Link href="https://www.healthfirst.com/emergency-readiness/emergency-medical-kits/" target="_blank">Browse our store</Link></StyledTypography>
                </Box>
              )
            }
            <div className="m-b-lg b-b" style={{ marginBottom: '15px' }}>
              <h3>Clinical Support Suite</h3>
            </div>
            <Box sx={{ padding: '15px' }}>
              <StyledTypography>You currently have no items. <Link href="https://www.healthfirst.com/customer-support/" target="_blank">Browse our store</Link></StyledTypography>
            </Box>
          </>
        )}
      </Box>
      <SubscriptionModal
        open={showSubscriptionModal}
        subscriptionItem={selectedItem}
        handleClose={handleCloseModal}
        enrolled={enrolled}
        customerNumber={customerNumber}
        currentUser={currentUser}
      />
    </Box>
  )
}

export default AutoRenewals;
